import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { SEO } from "@/components/seo/SEO";
import MarketingNav from "@/components/MarketingNav";
import { useIsMobile } from "@/hooks/use-mobile";

import PresetPicker from "./components/PresetPicker";
import HouseholdTable from "./components/HouseholdTable";
import AddItemForm from "./components/AddItemForm";
import SummaryCards from "./components/SummaryCards";
import CoverageRadial from "./components/CoverageRadial";
import ItemBreakdownChart from "./components/ItemBreakdownChart";
import DoomsdaySlider from "./components/DoomsdaySlider";
import TwoCheckTimeline from "./components/TwoCheckTimeline";
import LaborDepreciationCallout from "./components/LaborDepreciationCallout";
import CategoryDepreciationLegend from "./components/CategoryDepreciationLegend";
import ShareScenarioButton from "./components/ShareScenarioButton";
import ManifestCTA from "./components/ManifestCTA";

import { calculateTotals, formatUSD, type CalcItem } from "./utils/acvRcv";
import { getPreset, PRESET_HOUSEHOLDS, type HouseholdPreset } from "./utils/presetHouseholds";
import { encodeState, decodeState } from "./utils/urlState";

const DEFAULT_PRESET = "modest";
const CANONICAL = "https://manifestme.me/tools/acv-rcv-calculator";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  return reduced;
}

export default function AcvRcvCalculator() {
  const [searchParams, setSearchParams] = useSearchParams();
  const reducedMotion = usePrefersReducedMotion();
  const isMobile = useIsMobile();
  const [householdOpen, setHouseholdOpen] = useState(false);

  // Hydrate from URL or default
  const initial = useMemo(() => {
    const decoded = decodeState(searchParams.get("s"));
    if (decoded && decoded.items.length > 0) return decoded;
    const preset = getPreset(DEFAULT_PRESET);
    return { presetId: preset.id, items: preset.items, yearsForward: 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [items, setItems] = useState<CalcItem[]>(initial.items);
  const [presetId, setPresetId] = useState<string | null>(initial.presetId);
  const [yearsForward, setYearsForward] = useState(initial.yearsForward);

  // Persist to URL (debounced)
  useEffect(() => {
    const handle = setTimeout(() => {
      const token = encodeState(presetId, items, yearsForward);
      setSearchParams({ s: token }, { replace: true });
    }, 350);
    return () => clearTimeout(handle);
  }, [items, presetId, yearsForward, setSearchParams]);

  const totals = useMemo(() => calculateTotals(items, yearsForward), [items, yearsForward]);

  const handlePreset = (p: HouseholdPreset) => {
    setItems(p.items);
    setPresetId(p.id);
  };

  const handleAddItem = (item: CalcItem) => {
    setItems((prev) => [...prev, item]);
    setPresetId(null); // user has customized
  };

  const handleItemsChange = (next: CalcItem[]) => {
    setItems(next);
    setPresetId(null);
  };

  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/tools/acv-rcv-calculator?s=${encodeState(presetId, items, yearsForward)}`
    : CANONICAL;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "ACV vs RCV Insurance Calculator",
    applicationCategory: "FinanceApplication",
    operatingSystem: "Any",
    url: CANONICAL,
    description:
      "Free interactive calculator that models the gap between Actual Cash Value (ACV) and Replacement Cost Value (RCV) on home insurance claims. Use presets or add your own items.",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    publisher: { "@type": "Organization", name: "Manifest", url: "https://manifestme.me" },
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#111]">
      <SEO
        title="ACV vs RCV Insurance Calculator – See Your Coverage Gap | Manifest"
        description="Free interactive calculator. See how much your insurance would actually pay (ACV) vs what it costs to replace your stuff (RCV). Use presets or model your own household."
        canonical={CANONICAL}
        jsonLd={jsonLd}
      />

      {/* Nav */}
      <MarketingNav />

      <main className="mx-auto max-w-[1100px] px-5 py-10 md:py-14 space-y-10">
        {/* Hero */}
        <header className="animate-fade-in">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Link to="/blog/acv-vs-rcv-insurance" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" /> From the ACV vs RCV guide
            </Link>
            <span className="inline-flex items-center text-[11px] font-bold uppercase tracking-[0.08em] text-primary bg-primary/10 px-2.5 py-1 rounded-full leading-none">
              Free tool
            </span>
          </div>
          <h1 className="font-editorial text-[clamp(1.9rem,4.5vw,2.9rem)] leading-[1.1] tracking-tight max-w-3xl">
            How much would your insurance <span className="text-rose-600">actually</span> pay?
          </h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl leading-relaxed">
            Pick a household, edit it, or add your own items. We'll show you the gap between what
            it costs to replace your stuff (RCV) and what an ACV-only insurance policy would cut
            you a check for today.
          </p>
        </header>

        {/* Preset picker */}
        <section className="animate-fade-in">
          <SectionHeader
            title="Start from a preset"
            sub="Pick the closest household — customize it below."
            right={<ShareScenarioButton url={shareUrl} />}
          />
          <PresetPicker selectedId={presetId} onSelect={handlePreset} />
        </section>

        {/* Summary cards */}
        <section className="animate-fade-in">
          <SummaryCards totals={totals} />
        </section>

        {/* Household table */}
        <section className="animate-fade-in space-y-3">
          {isMobile ? (
            <>
              <button
                type="button"
                onClick={() => setHouseholdOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left active:bg-muted/40 transition-colors"
                aria-expanded={householdOpen}
              >
                <div className="min-w-0">
                  <div className="font-editorial text-lg leading-tight">Your household</div>
                  <div className="text-[12px] text-muted-foreground mt-0.5 truncate">
                    {items.length} items · {formatUSD(totals.rcv)} to replace
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs font-medium text-primary shrink-0">
                  {householdOpen ? "Hide" : "Edit"}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${householdOpen ? "rotate-180" : ""}`}
                  />
                </div>
              </button>
              {householdOpen && (
                <div className="space-y-3">
                  <HouseholdTable items={items} yearsForward={yearsForward} onChange={handleItemsChange} />
                  <AddItemForm onAdd={handleAddItem} />
                </div>
              )}
            </>
          ) : (
            <>
              <SectionHeader
                title="Your household"
                sub={`${items.length} items · Edit any cell, or add your own.`}
              />
              <HouseholdTable items={items} yearsForward={yearsForward} onChange={handleItemsChange} />
              <AddItemForm onAdd={handleAddItem} />
            </>
          )}
        </section>

        {/* Charts row */}
        <section className="animate-fade-in grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-5">
            <div className="text-sm font-semibold mb-1">Coverage at a glance</div>
            <div className="text-xs text-muted-foreground mb-2">
              How much of your replacement cost ACV actually covers.
            </div>
            <CoverageRadial totals={totals} reducedMotion={reducedMotion} />
            <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground">
              <LegendDot color="hsl(160 65% 42%)" label="ACV (covered)" />
              <LegendDot color="hsl(348 75% 58%)" label="Gap" />
            </div>
          </div>
          <div className="lg:col-span-3 rounded-2xl border border-border bg-card p-5">
            <div className="text-sm font-semibold mb-1">Where the gap lives</div>
            <div className="text-xs text-muted-foreground mb-2">
              Top 10 items by insurance gap. Sorted by where you'd lose the most.
            </div>
            <ItemBreakdownChart items={items} yearsForward={yearsForward} reducedMotion={reducedMotion} />
          </div>
        </section>

        {/* Doomsday slider */}
        <section className="animate-fade-in">
          <DoomsdaySlider value={yearsForward} onChange={setYearsForward} />
        </section>

        {/* Two-check timeline */}
        <section className="animate-fade-in">
          <TwoCheckTimeline totals={totals} />
        </section>

        {/* Labor depreciation callout */}
        <section className="animate-fade-in">
          <LaborDepreciationCallout />
        </section>

        {/* Category legend */}
        <section className="animate-fade-in">
          <CategoryDepreciationLegend />
        </section>

        {/* Manifest CTA */}
        <section className="animate-fade-in">
          <ManifestCTA totals={totals} />
        </section>

        {/* Footer disclaimer */}
        <p className="text-[11px] text-muted-foreground text-center max-w-2xl mx-auto leading-relaxed">
          Educational tool. Depreciation rates are based on publicly documented insurance industry tables
          ({PRESET_HOUSEHOLDS.length} preset households included) and are not a substitute for your
          carrier's actual schedule or a public adjuster's appraisal.
        </p>
      </main>

      <footer className="border-t border-[#E5E5E0] bg-[#FAFAF7]">
        <div className="mx-auto max-w-[1100px] px-5 py-8 flex items-center justify-between text-[12px] text-[#9CA3AF]">
          <span>© {new Date().getFullYear()} Manifest</span>
          <div className="flex gap-4">
            <Link to="/blog" className="hover:text-[#6B7280]">Blog</Link>
            <Link to="/terms" className="hover:text-[#6B7280]">Terms</Link>
            <Link to="/privacy" className="hover:text-[#6B7280]">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function SectionHeader({
  title,
  sub,
  right,
}: {
  title: string;
  sub?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3">
      <div>
        <h2 className="font-editorial text-xl tracking-tight">{title}</h2>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      {right}
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}
